import {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import {useLocation, useNavigate, useParams} from "react-router-dom";

import {CryptoStatisticList, Icon} from "@housebets/housebets-x-package";
import {IBottomMenuButtonProps} from "@housebets/housebets-x-package/lib/components/layout/Layout/View/components/BottomMenuMobile/Button/types";
import {TGeneralMenuList, TMenu, TTabsMenu} from "@housebets/housebets-x-package/lib/components/layout/Layout/View/components/Menu/types";
import {useLayoutContext} from "@housebets/housebets-x-package/lib/components/layout/Layout";

import useLiveChat from "app/hooks/useLiveChat";
import {setLoginType, setSearchGameModalOpen} from "app/store/slices/setting";
import {LoginType} from "app/types/common";
import apiAuth from "app/api/auth";
import {HomeIcon} from "@housebets/housebets-x-package/lib/components/icons";
import { useLangNavigate } from "app/hooks/useLangNavigate";
import { useDispatch } from "react-redux";
import { useAppSelector } from "app/hooks/useAppSelector";
import {config} from "app/config";

export const useWrapperData = () => {
    const {$t} = useIntl();
    const navigate = useNavigate();
    const param = useParams();
    const {pathname, search} = useLocation();
    const langNavigate = useLangNavigate();
    const dispatch = useDispatch();

    const platformConfig = useAppSelector((state) => state.config.platformConfig);
    const userData = useAppSelector((state) => state.user.user);
    const socialMediaList = useAppSelector((state) => state.config.platformConfig?.socialMediaList);
    const cryptoFuturesData = useAppSelector((state) => state.home.cryptoFuturesData);
    const isSearchGameModalOpen = useAppSelector((state) => state.setting.searchGameModalOpen);
    const {showLiveChat} = useLiveChat();
    const [asideActiveTabIndex, setAsideActiveTabIndex] = useState(-1);
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const {isModalMenuOpen, setIsModalMenuOpen, setIsAsideMenuOpen, isRightMenuOpen, setIsRightMenuOpen} = useLayoutContext();

    const loginType = useAppSelector((state) => state.setting.loginType);
    const isAuth = useAppSelector((state) => state.user.isLogin);

    const closeSearchGameModal = useCallback(() => {
        dispatch(setSearchGameModalOpen(false));
    }, []);

    const closeAuthModal = useCallback(() => {
        dispatch(setLoginType(LoginType.HIDE));
    }, []);

    const loggedTextData = useMemo(() => {
        const vipList = platformConfig?.vipList || [];
        const lastVip = vipList?.[vipList.length - 1];
        const maxLevel = lastVip?.level;
        const levelTitle = vipList?.find((v) => v.level === userData?.level)?.name;
        const levelNextTitle = vipList?.find((v) => v.level === (userData?.level ? userData?.level + 1 : 0))?.name;
        const matchName = levelTitle?.match(/^([^\d]+)\s*(\d+)$/);
        
        return {
            name: userData?.nick || "",
            level: userData?.level || 0,
            maxLevel: maxLevel || 0,
            levelTitle: levelTitle || "",
            levelTitleName: matchName && matchName[1] !== undefined 
             ? $t({ id: String(matchName[1].trim() || '') }) + ' ' + matchName[2]
            : levelTitle,
            levelNextTitle: levelNextTitle || "",
            amount: userData?.exp || 0,
        };
    }, [userData, platformConfig]);

    const socials = useMemo(() => {
        if (!socialMediaList?.length) return [];

        return socialMediaList?.map((v) => {
            const socialsIcon = v.linkUrl.includes("twitter")
                ? "NewTwitterIcon"
                : v.linkUrl.includes("Discord")
                  ? "DiscordIconV2"
                  : v.linkUrl.includes("t.me")
                    ? "TelegramIconV2"
                    : `${config.image_base_url}${v.logoUrl}`;
            return {
                href: v.linkUrl,
                icon: <Icon customIcon={socialsIcon} />,
            };
        });
    }, [socialMediaList]);

    const bottomMenu = useMemo<IBottomMenuButtonProps[]>(() => {
        const chatMenu: IBottomMenuButtonProps[] = isAuth && config.enable_chat
            ? [
                  {
                      icon: "chat",
                      isActive: isRightMenuOpen,
                      onClick: () => {
                          setIsRightMenuOpen(!isRightMenuOpen);
                          setIsModalMenuOpen(false);
                          setIsAsideMenuOpen(false);
                      },
                      title: $t({id: "Chat"}),
                  },
              ]
            : [];

        return [
            {
                icon: "menu",
                isActive: isModalMenuOpen,
                onClick: () => {
                    setIsAsideMenuOpen(!isModalMenuOpen);
                    setTimeout(() => setIsModalMenuOpen(!isModalMenuOpen), 100);
                    setIsRightMenuOpen(false);
                },
                title: $t({id: "Menu"}),
            },
            {
                icon: "sports",
                isActive: (pathname + search).includes("/sports"),
                onClick: () => {
                    navigate(langNavigate("/sports"));
                    setIsModalMenuOpen(false);
                    setIsRightMenuOpen(false);
                },
                title: $t({id: "Sports"}),
            },
            {
                icon: "casino",
                isActive: (pathname + search).includes("/gamelist"),
                onClick: () => {
                    navigate(langNavigate("/casino"));
                    setIsModalMenuOpen(false);
                    setIsRightMenuOpen(false);
                },
                title: $t({id: "Casino"}),
            },
            {
                icon: <Icon iconName="Vip" />,
                isActive: (pathname + search).includes("/vip"),
                onClick: () => {
                    navigate(langNavigate("/vip"));
                    setIsModalMenuOpen(false);
                    setIsRightMenuOpen(false);
                },
                title: $t({id: "Rewards"}),
            },
            // {
            //     icon: "crypto",
            //     isActive: (pathname + search).includes('/crypto-futures'),
            //     onClick: () => {
            //         navigate("/crypto-futures");
            //         setIsModalMenuOpen(false);
            //         setIsRightMenuOpen(false);
            //     },
            //     title: "Crypto",
            // },
            ...chatMenu,
        ];
    }, [isAuth, search, isModalMenuOpen, isRightMenuOpen, pathname, $t]);

    
    const hubMenu = useMemo<TMenu[]>(() => {
        // eslint-disable-next-line no-constant-condition
        const vipLink: TMenu[] = true
            ? [
                  {
                      href: langNavigate("/vip"),
                      isActive: (pathname + search).includes("/vip"),
                      icon: "Vip",
                      key: "defaultMenu_2",
                      kind: "vip",
                      text: $t({id: "Rewards"}),
                    //   name: loggedTextData?.levelTitle,
                        name: loggedTextData?.levelTitle || "rookie",
                      levelText: loggedTextData?.levelTitleName || $t({ id: "Rookie"}),
                      disableHover: true,
                  },
                  {
                      href: langNavigate("/affiliate"),
                      isActive: (pathname + search).includes("/affiliate"),
                      icon: "Affilate",
                      key: "defaultMenu_home_3",
                      kind: "link",
                      name: $t({id: "Affiliate"}),
                      additional: {
                          text: "",
                          icon: "UsersV3",
                          iconLeft: false,
                          backgroundColor: "transparent",
                      },
                  },
              ]
            : [];

        return [
            ...vipLink,
            {
                href: "https://blog.housebets.com/",
                icon: "Blog",
                key: "defaultMenu_4",
                kind: "link",
                name: $t({id: "Blog"}),
                onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open("https://blog.housebets.com/");
                },
            },
        ];
    }, [param, isAuth, search, loggedTextData, $t]);

    const defaultMenu = useMemo<TGeneralMenuList[]>(() => {
        return [
            {
                name: $t({id: "General"}),
                key: "list",
                list: [
                    {
                        key: "defaultMenu_0",
                        name: $t({id: "Home"}),
                        href: langNavigate("/"),
                        isActive: pathname === "/",
                        icon: <HomeIcon />,
                        kind: "link",
                    },
                    {
                        icon: "Favorite",
                        key: "defaultMenu_home_1",
                        kind: "link",
                        href: langNavigate("/gamelist/Favourite"),
                        isActive: (pathname + search).includes("/gamelist/Favourite"),
                        name: $t({id: "FavouriteGames"}),
                    },
                ],
            },
            {
                name: $t({id: "HUB"}),
                key: "list",
                list: hubMenu,
            },
            {
                name: $t({id: "HelpCenter"}),
                key: "list",
                list: [
                    {
                        href: langNavigate("/help/ResponsibleGambling"),
                        icon: "Politics",
                        key: "defaultMenu_5",
                        kind: "link",
                        isActive: (pathname + search).includes("/help/ResponsibleGambling"),
                        name: $t({id: "ResponsibleGambling"}),
                    },
                    {
                        href: "#",
                        icon: "LiveSupport2",
                        key: "defaultMenu_6",
                        kind: "link",
                        name: $t({id: "LiveSupport"}),
                        onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            showLiveChat();
                        },
                    },
                ],
            },
        ];
    }, [loggedTextData, $t]);

    const asideTabs = useMemo<TTabsMenu>(() => {
        return {
            activeIndex: asideActiveTabIndex,
            tabs: [
                {
                    name: $t({id: "Casino"}),
                    icon: "CasinoV2",
                    onClick: () => {
                        // setAsideActiveTabIndex(0);
                        navigate(langNavigate("/casino"));
                    },
                },
                {
                    name: $t({id: "Sports"}),
                    icon: "Sport",
                    onClick: () => {
                        // setAsideActiveTabIndex(1);
                        navigate(langNavigate("/sports"));
                    },
                },
                // {
                //     name: $t({ id: "Crypto" }),
                //     icon: 'CryptoFuturesV2',
                //     onClick: () => setAsideActiveTabIndex(2),
                // }
            ],
            defaultMenu,
            panels: [
                [
                    {
                        name: $t({id: "General"}),
                        key: "list",
                        list: [
                            {
                                key: "0_0",
                                name: $t({id: "Home"}),
                                href: langNavigate("/casino"),
                                isActive: pathname === "/gamelist",
                                icon: "Home",
                                kind: "link",
                            },
                            {
                                key: "0_1",
                                name: $t({id: "NewReleases"}),
                                href: langNavigate("/gamelist/new-releases"),
                                isActive: (pathname + search).includes("/gamelist/new-releases"),
                                icon: "NewReleases",
                                kind: "link",
                            },
                            {
                                icon: "Favorite",
                                key: "0_2",
                                kind: "link",
                                href: langNavigate("/gamelist/Favourite"),
                                isActive: (pathname + search).includes("/gamelist/Favourite"),
                                name: $t({id: "FavouriteGames"}),
                            },
                        ],
                    },
                    {
                        name: "",
                        key: "tabsList",
                        list: [
                            {
                                icon: "Casino",
                                key: "0_3",
                                kind: "tabLinkCollection",
                                links: [
                                    // {
                                    //     href: "/gamelist/Original",
                                    //     icon: "Original",
                                    //     name: $t({ id: "Originals" }),
                                    //     isActive: (pathname + search).includes('/gamelist/Original'),
                                    // },
                                    {
                                        href: langNavigate("/gamelist/Slots"),
                                        icon: "Slot",
                                        name: $t({id: "Slots"}),
                                        isActive: (pathname + search).includes("/gamelist/Slots"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/LiveCasino"),
                                        icon: "LiveCasino",
                                        name: $t({id: "LiveCasino"}),
                                        isActive: (pathname + search).includes("/gamelist/LiveCasino"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/BlackJack"),
                                        icon: "BlackJack",
                                        name: $t({id: "Blackjack"}),
                                        isActive: (pathname + search).includes("/gamelist/blackjack"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/table-game"),
                                        icon: "TableGames",
                                        name: $t({id: "TableGames"}),
                                        isActive: (pathname + search).includes("/gamelist/table-game"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/GameShows"),
                                        icon: "GameShows",
                                        name: $t({id: "GameShows"}),
                                        isActive: (pathname + search).includes("/gamelist/GameShows"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/Roulette"),
                                        icon: "Roulette",
                                        name: $t({id: "Roulette"}),
                                        isActive: (pathname + search).includes("/gamelist/Roulette"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/Baccarat"),
                                        icon: "Baccarat",
                                        name: $t({id: "Baccarat"}),
                                        isActive: (pathname + search).includes("/gamelist/Baccarat"),
                                    },
                                    {
                                        href: langNavigate("/gamelist/new-releases"),
                                        icon: "NewReleases",
                                        isActive: (pathname + search).includes("/gamelist/new-releases"),
                                        name: $t({id: "NewReleases"}),
                                    },
                                    // {
                                    //     href: '/gamelist/Favourite',
                                    //     icon: 'Roulette',
                                    //     isActive: (pathname + search).includes('/gamelist/Favourite'),
                                    //     name: $t({ id: "Favourite" }),
                                    // },
                                    {
                                        href: langNavigate("/gamelist/BonusBuy"),
                                        icon: "BonusBuy",
                                        isActive: (pathname + search).includes("/gamelist/BonusBuy"),
                                        name: $t({id: "BonusBuy"}),
                                    },
                                    {
                                        icon: 'MiniGames',
                                        href: langNavigate("/gamelist/MiniGames"),
                                        name: $t({id: "MiniGames"}),
                                        isActive: (pathname + search).includes("/MiniGames"),
                                    },
                                    {
                                        icon: 'Hot',
                                        href: langNavigate("/gamelist/hot"),
                                        name: $t({id: "Hot"}),
                                        isActive: (pathname + search).includes("/hot"),
                                    },
                                    {
                                        href: langNavigate("/providers"),
                                        icon: "ProvidersV2",
                                        name: $t({id: "Providers"}),
                                        isActive: (pathname + search).includes("/providers"),
                                    },
                                ],
                                name: $t({id: "CasinoGames"}),
                            },
                        ],
                    },
                    {
                        name: $t({id: "HUB"}),
                        key: "list",
                        list: hubMenu,
                    },
                    {
                        name: $t({id: "HelpCenter"}),
                        key: "list",
                        list: [
                            {
                                href: langNavigate("/help/ResponsibleGambling"),
                                icon: "Politics",
                                key: "0_8",
                                kind: "link",
                                name: $t({id: "ResponsibleGambling"}),
                                onClick: () => {
                                    navigate(langNavigate("/help/ResponsibleGambling"));
                                },
                            },
                            {
                                href: langNavigate("/"),
                                icon: "LiveSupport2",
                                key: "0_9",
                                kind: "link",
                                name: $t({id: "LiveSupport"}),
                                onClick: () => {
                                    showLiveChat();
                                },
                            },
                        ],
                    },
                ],
                [
                    {
                        name: $t({id: "General"}),
                        key: "list",
                        list: [
                            {
                                key: "1_0",
                                name: $t({id: "Home"}),
                                href: langNavigate("/sports"),
                                icon: "Home",
                                kind: "link",
                                isActive: pathname + search === "/sports",

                                // onClick: () => {
                                //     navigate("/sports?bt-path=%2F");
                                // },
                            },
                            {
                                key: "1_1",
                                name: $t({id: "UpcomingEvents"}),
                                href: langNavigate("/sports?bt-path=%2F"),
                                icon: "UpcomingEvents",
                                kind: "link",
                                isActive: pathname + search === "/sports?bt-path=%2F",
                            },
                            {
                                key: "1_2",
                                name: $t({id: "BetLive"}),
                                href: langNavigate("/sports?bt-path=%2Flive"),
                                icon: "BetLive",
                                kind: "link",
                                isActive: (pathname + search).includes("/sports?bt-path=%2Flive"),
                            },
                            {
                                key: "1_3",
                                name: $t({id: "MyBets"}),
                                href: langNavigate("/sports?bt-path=%2Fbets"),
                                icon: "MyBets",
                                kind: "link",
                                isActive: (pathname + search).includes("/sports?bt-path=%2Fbets"),
                            },
                        ],
                    },
                    {
                        name: "",
                        key: "tabsList",
                        list: [
                            {
                                icon: "Sport",
                                key: "1_4",
                                kind: "tabLinkCollection",
                                links: [
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fsoccer-1"),
                                        icon: "Soccer",
                                        name: $t({id: "Soccer"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fsoccer-1"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Ftennis-5"),
                                        icon: "Tennis",
                                        name: $t({id: "Tennis"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Ftennis-5"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F21"),
                                        icon: "Cricket",
                                        name: $t({id: "Cricket"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F21"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fbasketball-2"),
                                        icon: "BasketBall",
                                        name: $t({id: "Basketball"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fbasketball-2"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F16"),
                                        icon: "AmericanFootball",
                                        name: $t({id: "AmericanFootball"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F16"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fice-hockey-4"),
                                        icon: "Ice",
                                        name: $t({id: "IceHockey"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fice-hockey-4"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F117"),
                                        icon: "MMA",
                                        name: $t({id: "MMA"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F117"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F109"),
                                        icon: "CS2",
                                        name: $t({id: "CS2"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F109"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F40"),
                                        icon: "Formula",
                                        name: $t({id: "Formula1"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F40"),
                                    },
                                ],
                                name: $t({id: "SportsGames"}),
                            },
                        ],
                    },
                    {
                        name: $t({id: "Games"}),
                        key: "list",
                        list: [
                            {
                                icon: "AllSports",
                                key: "1_5",
                                kind: "linkCollection",
                                links: [
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fsoccer-1"),
                                        icon: "Soccer",
                                        name: $t({id: "Soccer"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fsoccer-1"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Ftennis-5"),
                                        icon: "Tennis",
                                        name: $t({id: "Tennis"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Ftennis-5"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F21"),
                                        icon: "Cricket",
                                        name: $t({id: "Cricket"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F21"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fbasketball-2"),
                                        icon: "BasketBall",
                                        name: $t({id: "Basketball"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fbasketball-2"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F16"),
                                        icon: "AmericanFootball",
                                        name: $t({id: "AmericanFootball"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F16"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fice-hockey-4"),
                                        icon: "Ice",
                                        name: $t({id: "IceHockey"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fice-hockey-4"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F117"),
                                        icon: "MMA",
                                        name: $t({id: "MMA"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F117"),
                                    },
                                ],
                                name: $t({id: "AllSports"}),
                            },
                            {
                                icon: "AllESports",
                                key: "1_6",
                                kind: "linkCollection",
                                links: [
                                    {
                                        href: langNavigate("/sports?bt-path=%2F109"),
                                        icon: "CS2",
                                        name: $t({id: "CS2"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F109"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F111"),
                                        icon: "Dota2",
                                        name: $t({id: "Dota2"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F111"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F110"),
                                        icon: "LOL",
                                        name: $t({id: "LeagueOfLegends"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F110"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2F112"),
                                        icon: "StarCraft",
                                        name: $t({id: "StarCraft2"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F112"),
                                    },
                                ],
                                name: $t({id: "AllESports"}),
                            },
                            {
                                icon: "AllRacing",
                                key: "1_7",
                                kind: "linkCollection",
                                links: [
                                    {
                                        href: langNavigate("/sports?bt-path=%2F55"),
                                        icon: "Horse",
                                        name: $t({id: "HorseRacing"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2F55"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fgreyhound-58"),
                                        icon: "Horse",
                                        name: $t({id: "Greyhound"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fgreyhound-58"),
                                    },
                                    {
                                        href: langNavigate("/sports?bt-path=%2Fharness-202"),
                                        icon: "Horse",
                                        name: $t({id: "Hamess"}),
                                        isActive: (pathname + search).includes("/sports?bt-path=%2Fharness-202"),
                                    },
                                ],
                                name: $t({id: "AllRacing"}),
                            },
                        ],
                    },
                    {
                        name: $t({id: "HUB"}),
                        key: "list",
                        list: hubMenu,
                    },
                    {
                        name: $t({id: "HelpCenter"}),
                        key: "list",
                        list: [
                            {
                                href: langNavigate("/help/ResponsibleGambling"),
                                icon: "Politics",
                                key: "1_12",
                                kind: "link",
                                name: $t({id: "ResponsibleGambling"}),
                                isActive: (pathname + search).includes("/help/ResponsibleGambling"),
                            },
                            {
                                href: "#",
                                icon: "LiveSupport2",
                                key: "1_13",
                                kind: "link",
                                name: $t({id: "LiveSupport"}),
                                onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    showLiveChat();
                                },
                            },
                        ],
                    },
                ],
                [
                    {
                        name: $t({id: "General"}),
                        key: "list",
                        list: [
                            {
                                key: "2_0",
                                name: $t({id: "Home"}),
                                href: langNavigate("/crypto-futures"),
                                icon: "Home",
                                kind: "link",
                                isActive: (pathname + search).includes("/crypto-futures"),
                            },
                        ],
                    },
                    {
                        name: "",
                        key: "tabsList",
                        list: [
                            {
                                key: "2_1",
                                kind: "node",
                                name: $t({id: "Cryptocurrencies"}),
                                icon: "CryptoFutures",
                                node: (
                                    <CryptoStatisticList
                                        list={[
                                            {
                                                amount: Number(cryptoFuturesData.btcData?.price.toFixed(2)),
                                                currency: "BTC",
                                                growth: Number(cryptoFuturesData.btcData?.stat24?.growth.toFixed(2)) >= 0 ? "up" : "down",
                                                icon: "/img/cards/crypto/btc.png",
                                                href: "/crypto-futures/BTC",
                                            },
                                            {
                                                amount: Number(cryptoFuturesData.ethData?.price.toFixed(2)),
                                                currency: "ETH",
                                                growth: Number(cryptoFuturesData.ethData?.stat24?.growth.toFixed(2)) >= 0 ? "up" : "down",
                                                icon: "/img/cards/crypto/eth.png",
                                                href: "/crypto-futures/ETH",
                                            },
                                            {
                                                amount: Number(cryptoFuturesData.solData?.price.toFixed(2)),
                                                currency: "SOL",
                                                growth: Number(cryptoFuturesData.solData?.stat24?.growth.toFixed(2)) >= 0 ? "up" : "down",
                                                icon: "/img/cards/crypto/sol.png",
                                                href: "/crypto-futures/SOL",
                                            },
                                            {
                                                amount: Number(cryptoFuturesData.ltcData?.price.toFixed(2)),
                                                currency: "LTC",
                                                growth: Number(cryptoFuturesData.ltcData?.stat24?.growth.toFixed(2)) >= 0 ? "up" : "down",
                                                icon: "/img/cards/crypto/ltc.png",
                                                href: "/crypto-futures/LTC",
                                            },
                                            {
                                                amount: Number(cryptoFuturesData.bnbData?.price.toFixed(2)),
                                                currency: "BNB",
                                                growth: Number(cryptoFuturesData.bnbData?.stat24?.growth.toFixed(2)) >= 0 ? "up" : "down",
                                                icon: "/img/cards/crypto/bnb.png",
                                                href: "/crypto-futures/BNB",
                                            },
                                        ]}
                                        onSearchChange={() => {}}
                                        onSubmit={() => {}}
                                        searchString=""
                                        withSearch={false}
                                    />
                                ),
                            },
                        ],
                    },
                    {
                        name: $t({id: "HUB"}),
                        key: "list",
                        list: hubMenu,
                    },
                    {
                        name: $t({id: "HelpCenter"}),
                        key: "list",
                        list: [
                            {
                                href: langNavigate("/help/ResponsibleGambling"),
                                icon: "Politics",
                                key: "2_6",
                                kind: "link",
                                name: $t({id: "ResponsibleGambling"}),
                                isActive: (pathname + search).includes("/help/ResponsibleGambling"),
                            },
                            {
                                href: "#",
                                icon: "LiveSupport2",
                                key: "2_7",
                                kind: "link",
                                name: $t({id: "LiveSupport"}),
                                onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    showLiveChat();
                                },
                            },
                        ],
                    },
                ],
            ],
        };
    }, [cryptoFuturesData, defaultMenu, asideActiveTabIndex, pathname, search, param, hubMenu, $t]);

    useEffect(() => {
        if (pathname.includes("/casino") || pathname.includes("/gamelist")) {
            setAsideActiveTabIndex(0);
            return;
        }
        if (pathname.includes("/sports")) {
            setAsideActiveTabIndex(1);
            return;
        }
        if (pathname === "/") {
            setAsideActiveTabIndex(-1);
        }
    }, [pathname, asideActiveTabIndex]);

    console.log('asideActiveTabIndex', asideActiveTabIndex);

    useEffect(() => {
        if (!isAuth) setIsRightMenuOpen(false);
    }, [isAuth]);

    useEffect(() => {
        const getRestrictLoginList = async () => {
            const {status, data} = await apiAuth.getRestrictLoginList();
            if (status === 200 && data.code === 0) {
                if (data.data.length) {
                    setIsAccessModalOpen(true);
                }
            }
        };
        getRestrictLoginList();
    }, []);

    return {
        asideTabs,
        socials,
        bottomMenu,
        pathname,
        isSearchGameModalOpen,
        closeSearchGameModal,
        isAccessModalOpen,
        setIsAccessModalOpen,
        loginType,
        closeAuthModal,
    };
};
